import './Login.scss';
import React, { ChangeEvent, FormEvent, useState } from 'react'
import { Button, Card, Container, Form, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { UserCredentials } from '../models/User';
import { Authentication } from '../api/Authentication';
import { useAuth } from '../contexts/auth';
import { useToasts } from '../contexts/toasts';
import { useFullScreenLoader } from '../contexts/fullScreenLoader';
import Cookies from 'js-cookie';
import { Link, Navigate } from 'react-router-dom';
import client from '../api/axios';
import LocaleSelector from '../components/LocaleSelector';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';

export default function Login() {
  const { t } = useTranslation();

  const [validated, setValidated] = useState<boolean>();
  const [user, setUser] = useState<UserCredentials>(new UserCredentials());
  const { token, setToken } = useAuth();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const handleSubmit = (evt: FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();

    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader!();

      Authentication.login(user)
        .then(
          ({ data }) => {
            setToken!(data.token);
            client.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            Cookies.set(window.__env.TOKEN_NAME, data.token, { expires: 1 });
            Cookies.set(window.__env.REF_TOKEN_NAME, data.refreshToken, { expires: 1 });
            hideLoader!();
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            hideLoader!();
            showToast!('login-error', message, `Login Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }

  const handleFieldChange = (field: keyof UserCredentials) => (evt: ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setUser({
      ...user,
      [field]: value,
    });
  }

  const cookie = Cookies.get(window.__env.TOKEN_NAME);

  if (token || cookie) return <Navigate to="/home" />;

  return (
    <Container className="login-container">
      <Card>
        <Row className="p-2">
          <div className="ms-auto w-auto">
            <LocaleSelector />
          </div>
        </Row>
        <Card.Body>
          <div className="text-center mb-3">
            <Logo size={120} />
            <Card.Title>Login</Card.Title>
            <Card.Subtitle className="text-muted">{t('login.message')}</Card.Subtitle>
          </div>

          <Form className="login-form" validated={validated} noValidate onSubmit={handleSubmit}>
            <Form.Floating className="mb-3">
              <Form.Control
                id="username-input"
                placeholder="Username"
                required
                value={user.username}
                onChange={handleFieldChange('username')}
              />
              <label htmlFor="email-input">Username</label>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: 'Username' })}</Form.Control.Feedback>
            </Form.Floating>

            <Form.Floating>
              <Form.Control
                id="password-input"
                type="password"
                placeholder="Password"
                value={user.password}
                onChange={handleFieldChange('password')}
                required
              />
              <label htmlFor="password-input">Password</label>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: 'Password' })}</Form.Control.Feedback>
            </Form.Floating>

            <div className="text-center my-3">
              <Link to="/forgot-password">{t('login.forgot')}</Link>
            </div>

            <div className="form-actions mb-3">
              <Button size="sm" type="submit">
                <FontAwesomeIcon icon={solid('sign-in')} />
                <span className="ms-2">{t('common.login')}</span>
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}